import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { READONLY } from '../lib/readonly';
import { useData, isDeleted } from '../lib/data';
import { useSelection } from '../lib/selection';
import { useToast } from '../lib/toast';
import { updateTaskWithRollback } from '../lib/taskWrites';
import type { Task, TaskComment, TaskPriority, TaskStatus } from '../lib/database.types';
import { DueChip, formatDueInputValue, parseDueInputValue } from '../components/DueChip';
import { ProjectPicker } from '../components/ProjectPicker';
import { AddTaskForm } from '../components/AddTaskForm';
import { BlockersPanel, writeBlockers } from '../components/BlockersPanel';
import type { Blocker } from '../lib/database.types';

const STATUSES: TaskStatus[] = ['pending', 'in_progress', 'blocked', 'completed', 'cancelled'];
const PRIORITIES: TaskPriority[] = ['critical', 'high', 'medium', 'low'];

interface PullTaskResult {
  ok: boolean;
  changed: boolean;
  code: string;
  message: string;
  command?: string;
  task_id?: string;
  build_id?: string;
  dispatch_id?: string;
  machine_name?: string;
  effect_class?: string;
  task_status?: TaskStatus;
  build_status?: string;
  dispatch_status?: string;
  details?: Record<string, unknown>;
}

function dispatchToast(result: PullTaskResult) {
  const suffix = result.machine_name ? ` · ${result.machine_name}` : '';
  if (result.changed) {
    return {
      kind: 'success' as const,
      title: `Task dispatched${suffix}`,
      body: result.message,
      ttlMs: 4500,
    };
  }
  if (result.ok) {
    return {
      kind: 'warn' as const,
      title: `Dispatch skipped · ${result.code}`,
      body: result.message,
      ttlMs: 5000,
    };
  }
  return {
    kind: 'error' as const,
    title: `Dispatch failed · ${result.code}`,
    body: result.message,
    ttlMs: 5000,
  };
}

export function TaskView({ taskId }: { taskId: string }) {
  const { allTasks, projectBySlug, clientBySlug, childrenByParent, patchTaskLocal } = useData();
  const { select } = useSelection();
  const { push } = useToast();

  const task = allTasks.find((t) => t.id === taskId);
  const [deleteConfirm, setDeleteConfirm] = useState<null | 'soft' | 'hard'>(null);
  const [typedTitle, setTypedTitle] = useState('');
  const [dispatching, setDispatching] = useState(false);
  const [lastDispatch, setLastDispatch] = useState<PullTaskResult | null>(null);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(true);
  // Stale-machine guard: read forge-01 heartbeat freshness so the dispatch
  // button can warn if the agent isn't alive. Refreshed once per TaskView
  // mount; that's enough — staleness changes on the order of minutes.
  const [forgeStaleMin, setForgeStaleMin] = useState<number | null>(null);
  useEffect(() => {
    supabase
      .from('factory_machines')
      .select('name,last_heartbeat')
      .eq('name', 'forge-01')
      .single()
      .then(({ data }) => {
        if (!data || !(data as { last_heartbeat: string | null }).last_heartbeat) {
          setForgeStaleMin(null);
          return;
        }
        const hb = new Date((data as { last_heartbeat: string }).last_heartbeat).getTime();
        const ageMin = Math.round((Date.now() - hb) / 60_000);
        setForgeStaleMin(ageMin);
      });
  }, []);

  useEffect(() => {
    let mounted = true;
    supabase
      .from('tb_task_comments')
      .select('*')
      .eq('task_id', taskId)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        if (!mounted) return;
        setComments((data as TaskComment[] | null) ?? []);
        setLoadingComments(false);
      });

    const sub = supabase
      .channel(`task-${taskId}-comments`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tb_task_comments',
          filter: `task_id=eq.${taskId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setComments((prev) => [...prev, payload.new as TaskComment]);
          } else if (payload.eventType === 'DELETE') {
            setComments((prev) => prev.filter((c) => c.id !== (payload.old as TaskComment).id));
          } else if (payload.eventType === 'UPDATE') {
            setComments((prev) =>
              prev.map((c) =>
                c.id === (payload.new as TaskComment).id ? (payload.new as TaskComment) : c,
              ),
            );
          }
        },
      )
      .subscribe();

    return () => {
      mounted = false;
      sub.unsubscribe();
    };
  }, [taskId]);

  if (!task) {
    return (
      <div className="flex-1 grid place-items-center text-dim font-mono text-sm">
        task not found: {taskId}
      </div>
    );
  }

  const project = projectBySlug[task.project_slug];
  const client = project?.client_slug ? clientBySlug[project.client_slug] : undefined;
  const kids = childrenByParent.get(task.id) ?? [];

  const patch = async (p: Partial<Task>) => {
    if (READONLY) return;
    await updateTaskWithRollback({
      task,
      patch: p,
      patchTaskLocal,
      push,
      persist: async () => {
        const { data, error } = await supabase
          .from('tb_tasks')
          .update(p)
          .eq('id', task.id)
          .select()
          .single();
        return { data: (data as Task | null) ?? null, error };
      },
    });
  };

  const toggleChild = async (sub: Task) => {
    if (READONLY) return;
    const next = sub.status === 'completed' ? 'pending' : 'completed';
    const nextCompletedAt = next === 'completed' ? new Date().toISOString() : null;
    await updateTaskWithRollback({
      task: sub,
      patch: {
        status: next,
        completed_at: nextCompletedAt,
      },
      patchTaskLocal,
      push,
      persist: async () => {
        const { data, error } = await supabase
          .from('tb_tasks')
          .update({
            status: next,
            completed_at: nextCompletedAt,
          })
          .eq('id', sub.id)
          .select()
          .single();
        return { data: (data as Task | null) ?? null, error };
      },
      errorTitle: 'Failed to update subtask',
    });
  };

  const addComment = async (body: string) => {
    if (!body.trim() || READONLY) return;
    const { error } = await supabase.from('tb_task_comments').insert({
      task_id: taskId,
      author: 'me',
      body: body.trim(),
    });
    if (error) {
      push({
        kind: 'error',
        title: 'Failed to post comment',
        body: error.message,
        ttlMs: 5000,
      });
    }
  };

  const softDelete = async () => {
    if (READONLY || !task) return;
    const nextTags = Array.isArray(task.tags) ? [...task.tags] : [];
    if (!nextTags.includes('deleted')) nextTags.push('deleted');
    const ok = await updateTaskWithRollback({
      task,
      patch: { tags: nextTags },
      patchTaskLocal,
      push,
      persist: async () => {
        const { data, error } = await supabase
          .from('tb_tasks')
          .update({ tags: nextTags })
          .eq('id', task.id)
          .select()
          .single();
        return { data: (data as Task | null) ?? null, error };
      },
      errorTitle: 'Failed to move task to trash',
    });
    if (!ok) return;
    setDeleteConfirm(null);
    select({ type: 'home' });
  };

  const restore = async () => {
    if (READONLY || !task) return;
    const nextTags = (Array.isArray(task.tags) ? task.tags : []).filter(
      (t) => t !== 'deleted',
    );
    await updateTaskWithRollback({
      task,
      patch: { tags: nextTags },
      patchTaskLocal,
      push,
      persist: async () => {
        const { data, error } = await supabase
          .from('tb_tasks')
          .update({ tags: nextTags })
          .eq('id', task.id)
          .select()
          .single();
        return { data: (data as Task | null) ?? null, error };
      },
      successToast: {
        kind: 'success',
        title: 'Task restored',
        body: task.title.slice(0, 60),
        ttlMs: 3000,
      },
      errorTitle: 'Failed to restore task',
    });
  };

  const hardDelete = async () => {
    if (READONLY || !task) return;
    if (typedTitle !== task.title) return;
    const { error } = await supabase.from('tb_tasks').delete().eq('id', task.id);
    if (error) {
      push({
        kind: 'error',
        title: 'Failed to delete task',
        body: error.message,
        ttlMs: 5000,
      });
      return;
    }
    setDeleteConfirm(null);
    setTypedTitle('');
    select({ type: 'home' });
  };

  // Dispatch this task to a factory machine via the orchestration RPC.
  // The RPC validates (pending, AI-routable, code_only, not blocked, has
  // context) and atomically creates a queued build + dispatch, then flips
  // the task to in_progress. Returns a structured result either way.
  const dispatchTask = async () => {
    if (READONLY || !task) return;
    setDispatching(true);
    setLastDispatch(null);
    try {
      const { data, error } = await supabase.rpc('rpc_orch_pull_task', {
        p_task_id: task.id,
        p_requested_by: 'task-board:andrew',
      });
      if (error) {
        const result: PullTaskResult = {
          ok: false,
          changed: false,
          code: 'rpc_error',
          message: error.message,
        };
        setLastDispatch(result);
        push(dispatchToast(result));
        return;
      }

      const result = (data as PullTaskResult | null) ?? {
        ok: false,
        changed: false,
        code: 'empty_response',
        message: 'RPC returned no payload',
      };
      setLastDispatch(result);
      if (result.changed && result.task_status) {
        patchTaskLocal(task.id, { status: result.task_status });
      }
      push(dispatchToast(result));
    } catch (error) {
      const result: PullTaskResult = {
        ok: false,
        changed: false,
        code: 'rpc_exception',
        message: error instanceof Error ? error.message : 'Unknown dispatch error',
      };
      setLastDispatch(result);
      push(dispatchToast(result));
    } finally {
      setDispatching(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-5xl p-8 space-y-6">
        <nav className="text-xs font-mono text-dim flex items-center gap-2 flex-wrap">
          <button onClick={() => select({ type: 'home' })} className="hover:text-ink">
            home
          </button>
          {client && (
            <>
              <span>›</span>
              <button
                onClick={() => select({ type: 'client', slug: client.slug })}
                className="hover:text-ink"
              >
                {client.display_name}
              </button>
            </>
          )}
          {project && (
            <>
              <span>›</span>
              <button
                onClick={() => select({ type: 'project', slug: project.slug })}
                className="hover:text-ink"
              >
                {project.name}
              </button>
            </>
          )}
          <span>›</span>
          <span className="text-muted">task · {task.id}</span>
        </nav>

        <header className="space-y-3">
          {isDeleted(task) && (
            <div className="panel p-3 border-red/40 bg-red/10 flex items-center justify-between gap-3">
              <div className="text-xs font-mono text-red">
                🗑 This task is in the trash
              </div>
              <div className="flex items-center gap-2">
                <button onClick={restore} className="btn text-xs">
                  restore
                </button>
                <button
                  onClick={() => setDeleteConfirm('hard')}
                  className="btn text-xs text-red hover:border-red/60"
                >
                  delete forever
                </button>
              </div>
            </div>
          )}
          <div className="flex items-start gap-4">
            <button
              onClick={async () => {
                if (READONLY) return;
                const next = task.status === 'completed' ? 'pending' : 'completed';
                await patch({
                  status: next,
                  completed_at: next === 'completed' ? new Date().toISOString() : null,
                });
              }}
              aria-label="toggle complete"
              className="check-hit shrink-0 mt-2"
              title={task.status === 'completed' ? 'reopen task' : 'mark complete'}
            >
              {task.status === 'completed' ? (
                <span className="check-dot-done">✓</span>
              ) : (
                <span className="check-dot" />
              )}
            </button>
            <div className="flex-1 min-w-0">
              <EditableTitle task={task} onSave={(v) => patch({ title: v })} />
              {task.status === 'completed' && (
                <div className="text-xs font-mono text-green mt-2">
                  ✓ completed {task.completed_at ? new Date(task.completed_at).toLocaleString() : ''}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusSelect
              value={task.status}
              onChange={(v) =>
                patch({
                  status: v,
                  completed_at: v === 'completed' ? new Date().toISOString() : null,
                })
              }
            />
            <PrioritySelect value={task.priority} onChange={(v) => patch({ priority: v })} />
            <DueEditor dueAt={task.due_at} onChange={(v) => patch({ due_at: v })} />
            <AutonomousToggle value={task.autonomous} onChange={(v) => patch({ autonomous: v })} />
            <ExecutionTargetSelect
              value={task.execution_target ?? 'me'}
              onChange={(v) => patch({ execution_target: v })}
            />
            <button
              onClick={() => setDeleteConfirm('soft')}
              className="ml-auto text-xs text-dim hover:text-red font-mono px-2 py-1"
              title="move this task to the trash (recoverable)"
            >
              🗑 delete
            </button>
          </div>
          <MoveTaskPicker task={task} onMoved={(newSlug) => patch({ project_slug: newSlug })} />

          <BlockersPanel
            task={task}
            onChange={async (next: Blocker[]) => {
              // Optimistic local patch first so the UI reflects immediately;
              // the DB write then lands via writeBlockers. If the write
              // fails, we rethrow and let BlockersPanel surface the error —
              // but we don't roll back the local state because the panel's
              // error display already tells the user something's wrong.
              patchTaskLocal(task.id, { blocked_by: next });
              await writeBlockers(task.id, next);
            }}
          />

          {/* Dispatch section — only relevant when task is AI-routable */}
          {task.status === 'pending' &&
            task.execution_target &&
            task.execution_target !== 'me' && (
              <div className="panel p-4 space-y-3 border-amber/30">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-amberHi flex items-center gap-2">
                      ⚡ Dispatch to forge
                    </div>
                    <div className="text-xs text-muted font-mono mt-0.5">
                      Runs on {task.execution_target} via factory-agent. Task
                      status flips to in_progress; forge-01 picks it up within
                      10 seconds.
                    </div>
                  </div>
                  <button
                    onClick={dispatchTask}
                    disabled={dispatching}
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-md text-sm font-semibold
                               bg-amber border border-amber text-bg
                               hover:brightness-110 transition
                               disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
                  >
                    {dispatching ? 'dispatching…' : '⚡ Run this'}
                  </button>
                </div>
                {forgeStaleMin !== null && forgeStaleMin > 10 && (
                  <div className="text-xs font-mono p-2 rounded border border-red/50 bg-red/10 text-red">
                    <div className="font-semibold">⚠ forge-01 heartbeat is {forgeStaleMin}m stale</div>
                    <div className="opacity-80 mt-0.5">
                      Last beat &gt; 10 min ago. The agent may be dead — dispatching now will queue a row that nothing picks up. SSH the VM and restart factory-agent before clicking Run this.
                    </div>
                  </div>
                )}
                {lastDispatch && (
                  <div
                    className={
                      'text-xs font-mono p-2 rounded border ' +
                      (lastDispatch.changed
                        ? 'text-green border-green/40 bg-green/10'
                        : lastDispatch.ok
                          ? 'text-amberHi border-amber/40 bg-amber/10'
                          : 'text-red border-red/40 bg-red/10')
                    }
                  >
                    <div className="font-semibold">{lastDispatch.code}</div>
                    <div className="opacity-80">{lastDispatch.message}</div>
                    {lastDispatch.build_id && (
                      <div className="opacity-60 mt-1">build_id: {lastDispatch.build_id}</div>
                    )}
                    {lastDispatch.machine_name && (
                      <div className="opacity-60">machine: {lastDispatch.machine_name}</div>
                    )}
                  </div>
                )}
              </div>
            )}

          {deleteConfirm === 'soft' && (
            <div className="panel p-4 space-y-3 border-red/40">
              <div className="text-sm">
                Move <span className="font-mono text-red">{task.title}</span> to the trash?
              </div>
              <div className="text-xs text-muted">
                The task stays in Supabase. You can restore it from the Deleted list.
                Subtasks and comments are preserved.
              </div>
              <div className="flex items-center gap-2 justify-end">
                <button onClick={() => setDeleteConfirm(null)} className="btn text-xs">
                  cancel
                </button>
                <button
                  onClick={softDelete}
                  className="btn text-xs border-red/60 text-red hover:bg-red/15"
                >
                  move to trash
                </button>
              </div>
            </div>
          )}

          {deleteConfirm === 'hard' && (
            <div className="panel p-4 space-y-3 border-red/60">
              <div className="text-sm text-red font-semibold">⚠ Permanent delete</div>
              <div className="text-xs text-muted">
                This deletes the row from Supabase. Subtasks and comments are cascaded.
                There is no undo. To confirm, type the exact task title below:
              </div>
              <div className="text-xs font-mono text-amberHi">{task.title}</div>
              <input
                type="text"
                value={typedTitle}
                onChange={(e) => setTypedTitle(e.target.value)}
                className="w-full px-3 py-2 bg-panelHi border border-red/40 rounded text-sm
                           placeholder-dim focus:outline-none focus:border-red font-mono"
                placeholder="type the title to confirm"
                autoFocus
              />
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => {
                    setDeleteConfirm(null);
                    setTypedTitle('');
                  }}
                  className="btn text-xs"
                >
                  cancel
                </button>
                <button
                  onClick={hardDelete}
                  disabled={typedTitle !== task.title}
                  className="btn text-xs border-red text-red hover:bg-red/15 disabled:opacity-30"
                >
                  delete forever
                </button>
              </div>
            </div>
          )}
        </header>

        <EditableDescription task={task} onSave={(v) => patch({ description: v })} />

        <section className="space-y-2">
          <div className="text-xs font-mono text-dim uppercase tracking-widest">
            Subtasks{' '}
            {kids.length > 0 && (
              <span className="text-amberHi">
                {kids.filter((k) => k.status === 'completed').length}/{kids.length}
              </span>
            )}
          </div>
          <div className="space-y-1">
            {kids.map((k) => (
              <div
                key={k.id}
                className="flex items-center gap-2 p-2 panel-hi rounded text-sm"
              >
                <button
                  onClick={() => toggleChild(k)}
                  aria-label="toggle subtask"
                  className="check-hit"
                >
                  <span
                    className={
                      'w-4 h-4 rounded border ' +
                      (k.status === 'completed'
                        ? 'bg-green border-green'
                        : 'border-border hover:border-amber')
                    }
                  />
                </button>
                <button
                  onClick={() => select({ type: 'task', id: k.id })}
                  className={
                    'flex-1 text-left truncate ' +
                    (k.status === 'completed' ? 'line-through text-dim' : '')
                  }
                >
                  {k.title}
                </button>
              </div>
            ))}
          </div>
          <AddTaskForm defaultProjectSlug={task.project_slug} parentTaskId={task.id} />
        </section>

        <section className="space-y-2">
          <div className="text-xs font-mono text-dim uppercase tracking-widest">
            Comments {comments.length > 0 && <span className="text-amberHi">{comments.length}</span>}
          </div>
          {loadingComments && <div className="text-xs text-dim italic">loading…</div>}
          <div className="space-y-2">
            {comments.map((c) => (
              <div key={c.id} className="panel-hi p-3 rounded space-y-1">
                <div className="flex items-baseline justify-between text-xs text-dim font-mono">
                  <span className="text-amberHi">{c.author}</span>
                  <span>{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap">{c.body}</div>
              </div>
            ))}
            {!loadingComments && comments.length === 0 && (
              <div className="text-xs text-dim italic">no comments yet</div>
            )}
          </div>
          <CommentInput onPost={addComment} />
        </section>
      </div>
    </div>
  );
}

function EditableTitle({ task, onSave }: { task: Task; onSave: (v: string) => void }) {
  const [val, setVal] = useState(task.title);
  const [editing, setEditing] = useState(false);
  useEffect(() => setVal(task.title), [task.title]);
  if (!editing) {
    return (
      <h1
        onClick={() => setEditing(true)}
        className="text-3xl font-semibold tracking-tight cursor-text hover:bg-panelHi/60 -mx-2 px-2 py-1 rounded"
      >
        {task.title}
      </h1>
    );
  }
  return (
    <input
      autoFocus
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        setEditing(false);
        if (val.trim() && val !== task.title) onSave(val.trim());
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setVal(task.title);
          setEditing(false);
        }
      }}
      className="w-full text-3xl font-semibold bg-panelHi border border-amber/60 rounded px-2 py-1 focus:outline-none"
    />
  );
}

function EditableDescription({
  task,
  onSave,
}: {
  task: Task;
  onSave: (v: string) => void;
}) {
  const [val, setVal] = useState(task.description ?? '');
  const [editing, setEditing] = useState(false);
  useEffect(() => setVal(task.description ?? ''), [task.description]);

  return (
    <div className="space-y-1">
      <div className="text-xs font-mono text-dim uppercase tracking-widest">Description</div>
      {!editing ? (
        <div
          onClick={() => setEditing(true)}
          className="text-sm text-muted whitespace-pre-wrap cursor-text hover:bg-panelHi/40
                     border border-transparent hover:border-border rounded p-3 min-h-[3rem]"
        >
          {task.description || (
            <span className="text-dim italic">add a description…</span>
          )}
        </div>
      ) : (
        <textarea
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={() => {
            setEditing(false);
            if (val !== task.description) onSave(val);
          }}
          rows={Math.min(12, Math.max(4, val.split('\n').length))}
          className="w-full text-sm bg-panelHi border border-amber/60 rounded p-3 focus:outline-none font-mono"
        />
      )}
    </div>
  );
}

function StatusSelect({ value, onChange }: { value: TaskStatus; onChange: (v: TaskStatus) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as TaskStatus)}
      className="px-3 py-2 bg-panelHi border border-border rounded text-xs font-mono"
    >
      {STATUSES.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );
}

function PrioritySelect({
  value,
  onChange,
}: {
  value: TaskPriority;
  onChange: (v: TaskPriority) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as TaskPriority)}
      className="px-3 py-2 bg-panelHi border border-border rounded text-xs font-mono"
    >
      {PRIORITIES.map((p) => (
        <option key={p} value={p}>
          {p}
        </option>
      ))}
    </select>
  );
}

function DueEditor({
  dueAt,
  onChange,
}: {
  dueAt: string | null;
  onChange: (v: string | null) => void;
}) {
  const [show, setShow] = useState(false);
  if (!show) {
    return (
      <button
        className="px-3 py-2 bg-panelHi border border-border rounded text-xs font-mono hover:border-amber/40"
        onClick={() => setShow(true)}
      >
        {dueAt ? <DueChip dueAt={dueAt} /> : 'set due date'}
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <input
        type="date"
        autoFocus
        value={formatDueInputValue(dueAt)}
        onChange={(e) => onChange(parseDueInputValue(e.target.value))}
        onBlur={() => setShow(false)}
        className="px-3 py-2 bg-panelHi border border-amber/60 rounded text-xs font-mono"
      />
      {dueAt && (
        <button
          className="text-xs text-dim hover:text-red"
          onClick={() => {
            onChange(null);
            setShow(false);
          }}
        >
          ×
        </button>
      )}
    </span>
  );
}

function ExecutionTargetSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 bg-panelHi border border-border rounded text-xs font-mono"
      title="who runs this task"
    >
      <option value="me">run by me</option>
      <option value="forge-01">forge-01 (vm)</option>
      <option value="laptop">laptop</option>
    </select>
  );
}

function AutonomousToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      className={
        'px-3 py-2 border rounded text-xs font-mono transition-colors ' +
        (value
          ? 'bg-amber/20 border-amber/70 text-white'
          : 'bg-panelHi border-border text-dim hover:border-borderHi')
      }
      onClick={() => onChange(!value)}
    >
      {value ? '◆ agent' : 'agent?'}
    </button>
  );
}

function MoveTaskPicker({
  task,
  onMoved,
}: {
  task: Task;
  onMoved: (newSlug: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="text-xs font-mono text-dim hover:text-amberHi px-2 py-1"
        title="move this task to a different build"
      >
        ↪ move to another build
      </button>
    );
  }
  return (
    <div className="space-y-2 panel-hi p-3 rounded">
      <div className="text-[10px] font-mono text-dim uppercase tracking-widest">
        Move this task
      </div>
      <ProjectPicker
        value={task.project_slug}
        onChange={(newSlug) => {
          if (newSlug && newSlug !== task.project_slug) onMoved(newSlug);
          setEditing(false);
        }}
        placeholder="search builds…"
      />
      <button
        onClick={() => setEditing(false)}
        className="text-xs font-mono text-dim hover:text-ink"
      >
        cancel
      </button>
    </div>
  );
}

function CommentInput({ onPost }: { onPost: (b: string) => void }) {
  const [val, setVal] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!val.trim()) return;
        onPost(val);
        setVal('');
      }}
      className="space-y-2"
    >
      <textarea
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="add a comment or work log entry…"
        rows={3}
        className="w-full px-3 py-3 bg-panelHi border border-border rounded text-sm placeholder-dim focus:outline-none focus:border-amber/60 font-mono"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit();
          }
        }}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-dim font-mono">⌘+enter</span>
        <button type="submit" disabled={!val.trim()} className="btn-primary text-xs">
          post comment
        </button>
      </div>
    </form>
  );
}
